/**
 * MedicationSafetyPanel — physician-facing contraindication warnings.
 *
 * Embeddable component. Caller passes the patient + proposed prescription;
 * we call medications:reconcile, render the warnings with severity-coded
 * styling, and capture the physician's disposition (heeded / overridden
 * with required reason / no-warning auto-recorded).
 *
 * Drop-in usage:
 *   <MedicationSafetyPanel
 *      patientId={42}
 *      proposed={{ drugName, dosage, frequency, route }}
 *      clinicalNoteId={noteId}   // optional, ties the audit row to the note
 *      onDispositionRecorded={() => ...}
 *   />
 *
 * Design constraint: the panel never PREVENTS prescribing. It informs.
 * Physician makes the call. Override-with-reason is audit-logged.
 *
 * See routes/medication_safety.js + migration 095.
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Shield, ShieldAlert, ShieldX, AlertCircle, CheckCircle2, Loader2,
  Sparkles, Pill,
} from "lucide-react";

const SEVERITY_STYLE = {
  info:     { bg: "#e8f0fb", border: "#c9d6e8", color: "#1f6feb", label: "INFO",     I: Shield },
  caution:  { bg: "#fef6e0", border: "#e0b973", color: "#7c5210", label: "CAUTION",  I: Shield },
  warning:  { bg: "#fde8e8", border: "#f5a5a5", color: "#b00020", label: "WARNING",  I: ShieldAlert },
  critical: { bg: "#fcdcdc", border: "#d24747", color: "#7a0014", label: "CRITICAL", I: ShieldX },
};

const CATEGORY_LABEL = {
  drug_drug:                  "Drug-Drug Interaction",
  drug_allergy:               "Drug-Allergy Interaction",
  duplicate_therapy:          "Duplicate Therapy",
  dosing:                     "Dosing Concern",
  contraindication_condition: "Diagnosis Contraindication",
  contraindication_age:       "Age-based Contraindication",
  ai_concern:                 "AI-Identified Concern",
};

export default function MedicationSafetyPanel({
  patientId, proposed, clinicalNoteId, encounterId,
  onDispositionRecorded, autoRun,
}) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [decisionBusy, setDecisionBusy] = useState(false);
  const [decisionMsg, setDecisionMsg] = useState(null);

  const canCheck = !!(patientId && proposed && proposed.drugName);

  const runCheck = useCallback(async () => {
    if (!canCheck) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setDecisionMsg(null);
    try {
      const resp = await window.zignagenixAPI.invoke("medications:reconcile", {
        patientId,
        drugName: proposed.drugName,
        dosage: proposed.dosage,
        frequency: proposed.frequency,
        route: proposed.route,
        clinicalNoteId: clinicalNoteId || null,
        encounterId: encounterId || null,
      });
      if (resp && resp.error) {
        setError(resp.message || resp.code || "Reconciliation failed");
      } else {
        setResult(resp);
        // If the engine produced zero warnings, auto-record the no_warning
        // disposition so the audit row has a complete state.
        if (resp.overall_severity === "info" || resp.warning_count === 0) {
          await window.zignagenixAPI.invoke("medications:record-safety-decision", {
            decisionId: resp.decision_id,
            disposition: "no_warning",
          });
        }
      }
    } catch (e) {
      setError((e && e.message) || "Reconciliation failed");
    }
    setLoading(false);
  }, [canCheck, patientId, proposed, clinicalNoteId, encounterId]);

  useEffect(() => {
    if (autoRun && canCheck) runCheck();
    // eslint-disable: only depend on auto/canCheck/runCheck identity
  }, [autoRun, canCheck, runCheck]);

  async function heeded() {
    if (!result || !result.decision_id) return;
    setDecisionBusy(true);
    setDecisionMsg(null);
    try {
      const r = await window.zignagenixAPI.invoke("medications:record-safety-decision", {
        decisionId: result.decision_id,
        disposition: "heeded",
      });
      if (r && r.error) setDecisionMsg({ kind: "error", text: r.message || r.code });
      else {
        setDecisionMsg({ kind: "ok", text: "Recorded: warnings heeded (prescription modified or cancelled)." });
        if (onDispositionRecorded) onDispositionRecorded({ disposition: "heeded", decision_id: result.decision_id });
      }
    } catch (e) {
      setDecisionMsg({ kind: "error", text: (e && e.message) || "Failed" });
    }
    setDecisionBusy(false);
  }

  async function overridden() {
    if (!result || !result.decision_id) return;
    const reason = window.prompt(
      "Override these warnings? You're proceeding with the prescription despite the safety concerns above.\n\n" +
      "Required: explain your clinical reasoning (≥10 chars). This will be audit-logged."
    );
    if (!reason) return;
    if (reason.trim().length < 10) {
      setDecisionMsg({ kind: "error", text: "Override reason must be at least 10 characters." });
      return;
    }
    setDecisionBusy(true);
    setDecisionMsg(null);
    try {
      const r = await window.zignagenixAPI.invoke("medications:record-safety-decision", {
        decisionId: result.decision_id,
        disposition: "overridden",
        overrideReason: reason.trim(),
      });
      if (r && r.error) setDecisionMsg({ kind: "error", text: r.message || r.code });
      else {
        setDecisionMsg({ kind: "ok", text: "Override recorded for audit. Prescription may proceed." });
        if (onDispositionRecorded) onDispositionRecorded({ disposition: "overridden", reason: reason.trim(), decision_id: result.decision_id });
      }
    } catch (e) {
      setDecisionMsg({ kind: "error", text: (e && e.message) || "Failed" });
    }
    setDecisionBusy(false);
  }

  if (!canCheck) {
    return (
      <div style={emptyStyle}>
        <Pill size={14} color="#9aa4b2" />
        <span>Enter a drug name to check for contraindications.</span>
      </div>
    );
  }

  return (
    <div style={{ background: "#fff", border: "1px solid #e1e6ed", borderRadius: 4, padding: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ShieldAlert size={16} color="#1f6feb" />
          <strong style={{ fontSize: 13 }}>Medication Safety Check</strong>
          <span style={{ fontSize: 11, color: "#5a6478" }}>
            Bedrock-augmented structured rule lookup. <em>AI never blocks — physician is the final authority.</em>
          </span>
        </div>
        <button onClick={runCheck} disabled={loading} className="btn-primary" style={{ fontSize: 12 }}>
          {loading ? <><Loader2 size={12} className="spin" /> Checking…</> : "Run safety check"}
        </button>
      </div>

      {error && (
        <div style={alertStyle("error")}><AlertCircle size={12} /> {error}</div>
      )}

      {!result && !loading && !error && (
        <div style={{ fontSize: 12, color: "#9aa4b2", fontStyle: "italic", padding: "12px 4px" }}>
          Click <strong>Run safety check</strong> to scan the proposed prescription against the patient's current medications, allergies, and active diagnoses.
        </div>
      )}

      {result && (
        <>
          <ResultBanner result={result} />
          {(result.warnings || []).length > 0 && (
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
              {result.warnings.map((w, i) => (
                <WarningCard key={i} warning={w} />
              ))}
            </div>
          )}

          {/* ── Disposition controls (only if warnings present and not yet decided) */}
          {(result.warnings || []).length > 0 && !decisionMsg && (
            <div style={{ marginTop: 12, padding: 10, background: "#f9fafc", borderRadius: 4, border: "1px solid #e1e6ed" }}>
              <div style={{ fontSize: 11, color: "#5a6478", marginBottom: 8 }}>
                <strong>Disposition</strong> — record your decision for the audit log. This is the clinical malpractice defense trail.
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <button onClick={heeded} disabled={decisionBusy} style={btnHeed}>
                  <CheckCircle2 size={12} /> Heed warnings (modify or cancel prescription)
                </button>
                <button onClick={overridden} disabled={decisionBusy} style={btnOverride}>
                  <AlertCircle size={12} /> Override and prescribe anyway (requires reason)
                </button>
              </div>
            </div>
          )}

          {decisionMsg && (
            <div style={{ ...alertStyle(decisionMsg.kind), marginTop: 10 }}>
              {decisionMsg.kind === "ok" ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
              {decisionMsg.text}
            </div>
          )}
        </>
      )}

      <div style={{ marginTop: 10, fontSize: 10, color: "#9aa4b2" }}>
        Every check + disposition is audit-logged to medication_safety_decisions (HIPAA §164.312(b)).
        AI calls route through Bedrock inside the BAA boundary — no PHI leaves AWS.
      </div>
    </div>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────

function ResultBanner({ result }) {
  const s = SEVERITY_STYLE[result.overall_severity] || SEVERITY_STYLE.info;
  const I = s.I;
  return (
    <div style={{
      background: s.bg, border: `1px solid ${s.border}`, color: s.color,
      padding: "8px 12px", borderRadius: 4,
      display: "flex", alignItems: "center", gap: 10,
    }}>
      <I size={18} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>
          {result.overall_severity === "info"
            ? "No safety concerns surfaced"
            : `${result.warning_count} ${s.label.toLowerCase()} ${result.warning_count === 1 ? "issue" : "issues"} identified`}
        </div>
        {result.ai_used && (
          <div style={{ fontSize: 10, marginTop: 2, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Sparkles size={10} /> AI layered on structured rules · model {result.ai_model || "Bedrock"}
          </div>
        )}
      </div>
    </div>
  );
}

function WarningCard({ warning }) {
  const s = SEVERITY_STYLE[warning.severity] || SEVERITY_STYLE.caution;
  return (
    <div style={{
      background: "#fff", border: `1px solid ${s.border}`,
      borderLeft: `4px solid ${s.color}`,
      padding: 10, borderRadius: 3,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        <span style={{
          fontSize: 10, fontWeight: 700, color: "#fff",
          background: s.color, padding: "2px 6px", borderRadius: 3,
          textTransform: "uppercase", letterSpacing: 0.4,
        }}>{s.label}</span>
        <span style={{ fontSize: 11, color: "#5a6478" }}>
          {CATEGORY_LABEL[warning.category] || warning.category}
        </span>
        {warning.source === "ai" && (
          <span style={{
            fontSize: 10, color: "#6b1f8b", display: "inline-flex", alignItems: "center", gap: 3,
            background: "#f4e8fb", padding: "1px 6px", borderRadius: 3,
          }}>
            <Sparkles size={10} /> AI
          </span>
        )}
        {warning.conflicting_with && (
          <span style={{ fontSize: 10, color: "#9aa4b2", marginLeft: "auto" }}>
            with: {warning.conflicting_with}
          </span>
        )}
      </div>
      <div style={{ fontSize: 13, color: "#1f2a3b", lineHeight: 1.4 }}>
        {warning.message}
      </div>
      {warning.recommendation && (
        <div style={{ marginTop: 6, fontSize: 12, color: "#3a4a5e", fontStyle: "italic" }}>
          <strong>Recommended:</strong> {warning.recommendation}
        </div>
      )}
    </div>
  );
}

const emptyStyle = {
  padding: 10, background: "#f9fafc", border: "1px solid #e1e6ed", borderRadius: 4,
  fontSize: 12, color: "#5a6478",
  display: "flex", alignItems: "center", gap: 6,
};
const btnHeed = {
  padding: "5px 10px", fontSize: 12, fontWeight: 600,
  background: "#2d6b2d", color: "#fff", border: "none", borderRadius: 3, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 4,
};
const btnOverride = {
  padding: "5px 10px", fontSize: 12, fontWeight: 600,
  background: "#fff", color: "#b00020", border: "1px solid #f5a5a5", borderRadius: 3, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 4,
};
function alertStyle(kind) {
  return {
    padding: "6px 10px", borderRadius: 3, fontSize: 12, display: "flex", alignItems: "center", gap: 6,
    background: kind === "ok" ? "#e9f5e9" : "#fde8e8",
    border: kind === "ok" ? "1px solid #a5d5a5" : "1px solid #f5a5a5",
    color: kind === "ok" ? "#2d6b2d" : "#8b0000",
  };
}
